import { useCallback, useContext, useEffect, useState } from "react";
import { buildClasses } from "../utils/buildClasses";
import { RyeMainContentSpacer } from "./RyeMainContentSpacer";
import {
  ScrollContainerContext,
  STANDARD_MAIN_CONTENT_CARD_PADDING_X,
} from "./RyeMainContentContainerBase";
import {
  RyeCenteredWidth,
  ryeCenteredContentClasses,
} from "./RyeMainContentHeader";

// How far the scroll shadow extends past the top bar's content, tapering to
// nothing over that distance.
const OVERHANG_PX = 12;
const MASK = `linear-gradient(to right, transparent, black ${OVERHANG_PX}px, black calc(100% - ${OVERHANG_PX}px), transparent)`;

/**
 * The gap between a top bar and its scrolling body, where a soft shadow fades
 * in once the body is scrolled (`visible`), showing that content is passing
 * beneath a bar that stays put. It spans the bar's content, in its centred
 * column if it has one, at full strength, tapering off over a slight overhang
 * at each end. With `edge="bottom"` it sits in the gap above a bottom bar
 * instead, casting upward while there's more content below.
 */
export function RyeMainContentScrollShadow({
  visible,
  centered,
  edge = "top",
}: {
  visible: boolean;
  centered?: RyeCenteredWidth;
  edge?: "top" | "bottom";
}): JSX.Element {
  return (
    <RyeMainContentSpacer direction="h">
      <div
        className={buildClasses(
          "pointer-events-none",
          "absolute",
          "inset-x-0",
          edge === "top" ? "top-0" : "bottom-0",
          STANDARD_MAIN_CONTENT_CARD_PADDING_X,
        )}
      >
        <div
          className={buildClasses(
            centered ? ryeCenteredContentClasses(centered) : "",
            "relative",
          )}
        >
          <div
            className={buildClasses(
              "absolute",
              edge === "top" ? "top-0" : "bottom-0",
              "h-[3px]",
              edge === "top" ? "bg-gradient-to-b" : "bg-gradient-to-t",
              "from-[rgba(40,30,10,0.07)]",
              "to-transparent",
              "transition-opacity",
              "duration-200",
              visible ? "opacity-100" : "opacity-0",
            )}
            style={{
              left: -OVERHANG_PX,
              right: -OVERHANG_PX,
              maskImage: MASK,
              WebkitMaskImage: MASK,
            }}
          />
        </div>
      </div>
    </RyeMainContentSpacer>
  );
}

/**
 * Whether a scrolling body is scrolled from its top, and whether it has more
 * content below, for showing a RyeMainContentScrollShadow at either edge. Pass
 * `onScroll` to a RyeScrollListener in the body.
 */
export function useRyeScrolled(): {
  scrolled: boolean;
  moreBelow: boolean;
  onScroll: (scrollElement: HTMLElement) => void;
} {
  const [scrolled, setScrolled] = useState(false);
  const [moreBelow, setMoreBelow] = useState(false);
  const onScroll = useCallback((scrollElement: HTMLElement) => {
    setScrolled(scrollElement.scrollTop > 0);
    // A pixel of slack for fractional scroll positions at the very end.
    setMoreBelow(
      scrollElement.scrollHeight -
        scrollElement.scrollTop -
        scrollElement.clientHeight >
        1,
    );
  }, []);
  return { scrolled, moreBelow, onScroll };
}

/**
 * Calls `onScroll` with the enclosing scrolling body whenever it scrolls or it
 * or its content resizes, and once on mount. Place it inside the body, e.g. a
 * RyeMainContentCard.
 */
export function RyeScrollListener({
  onScroll,
}: {
  onScroll: (scrollElement: HTMLElement) => void;
}): null {
  const scrollElement = useContext(ScrollContainerContext);
  useEffect(() => {
    if (!scrollElement) return;
    const handle = () => onScroll(scrollElement);
    handle();
    scrollElement.addEventListener("scroll", handle, { passive: true });
    // Content arriving, filtering or the body resizing changes what's below
    // without a scroll event.
    const resizeObserver = new ResizeObserver(handle);
    const observeChildren = () => {
      resizeObserver.disconnect();
      resizeObserver.observe(scrollElement);
      for (const child of Array.from(scrollElement.children)) {
        resizeObserver.observe(child);
      }
    };
    observeChildren();
    const mutationObserver = new MutationObserver(observeChildren);
    mutationObserver.observe(scrollElement, { childList: true });
    return () => {
      scrollElement.removeEventListener("scroll", handle);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [scrollElement, onScroll]);
  return null;
}
